/**
 * The rule that turns a drag on the scrub bar into a marked range.
 *
 * Split out of `useProgressBar` rather than left beside its only caller: the hook reaches the
 * tracking store and, through it, the API client, so nothing in it can be exercised without
 * booting the app. This is the one piece of the gesture that is a decision rather than
 * plumbing — it encodes the narrowest range a drag may commit — so it is the piece worth
 * pinning down. Pure and dependency-free for that reason, the same arrangement as
 * `clipGeometry.ts`; see `__tests__`.
 */
import type { SelectionSpan } from '../types';

/**
 * The narrowest range a drag may produce, as a percentage of the bar.
 *
 * A guard on *output*, not on input: a drag that crosses the threshold and then comes back
 * would otherwise commit a hairline range that cannot be grabbed again by either handle, and
 * the only way out would be to clear and start over. Expressed in percent because that is the
 * only unit this layer has — 0.5% of a 10-minute media is 3 seconds, of a 30-second clip it is
 * 0.15s, and in both cases it is about 3px of bar, which is what actually matters for grabbing
 * a handle back.
 */
export const MIN_SELECTION_PERCENT = 0.5;

/**
 * Whether an existing range is wide enough for this bar to adjust, and therefore whether the bar
 * offers handles on it at all.
 *
 * The same threshold read the other way round. A drag may not *commit* a range below it, so a
 * range already below it cannot be nudged — every adjustment the gesture can express is a jump
 * to at least `MIN_SELECTION_PERCENT` of the media, anchored on the opposite edge. Offering
 * handles there promises an edit the bar cannot make, and taking the promise up throws the range
 * across the media: a two-second moment in a 45-minute media is 0.44px of a 600px bar, its two
 * handles are drawn on top of each other, and one pixel of that bar is four and a half seconds.
 *
 * Below the threshold the band is still drawn — the range stays visible, and a press still
 * starts a *new* range, which is the one edit the bar can honestly offer. Adjusting a range this
 * short is the filmstrip's job, because the filmstrip can zoom and the scrub bar cannot.
 */
export const isAdjustable = (selection: SelectionSpan): boolean =>
  selection.endPercent - selection.startPercent >= MIN_SELECTION_PERCENT;

/**
 * Order the two ends of a drag and reject a span too thin to grab a handle on.
 *
 * The ends arrive unordered by construction — a drag leftwards from the anchor gives `b < a` —
 * so this is where a pair of percentages becomes a range. Returns `null` for a span below the
 * threshold rather than widening it: the viewer's gesture is reported as having marked
 * nothing, which is recoverable, where a silently widened range is not what they drew.
 */
export const spanFrom = (a: number, b: number): SelectionSpan | null => {
  const startPercent = Math.min(a, b);
  const endPercent = Math.max(a, b);
  if (endPercent - startPercent < MIN_SELECTION_PERCENT) return null;
  return { startPercent, endPercent };
};

/**
 * Which edge a press adjusts, expressed as the percentage of the edge that stays put.
 *
 * `null` means the press was not on a handle, and the caller starts a new range from it.
 *
 * Returning the *anchor* rather than which handle was grabbed is what lets one move handler
 * serve both cases: adjusting an edge is marking a range whose other end is already decided.
 *
 * The nearer handle wins outright. On a band narrower than twice the tolerance — a 10-second
 * moment in a 10-minute media is about 1.5% of the bar — both handles are in reach from
 * everywhere inside it, so "which is nearer" is the only question that separates them, and
 * getting it wrong moves the edge the viewer is not touching. Ties go to the start, arbitrarily:
 * they only happen at the exact midpoint of a band that narrow.
 */
export const anchorForGrab = (
  percent: number,
  selection: SelectionSpan,
  tolerancePercent: number,
): number | null => {
  const toStart = Math.abs(percent - selection.startPercent);
  const toEnd = Math.abs(percent - selection.endPercent);
  if (Math.min(toStart, toEnd) > tolerancePercent) return null;
  return toStart <= toEnd ? selection.endPercent : selection.startPercent;
};
